import { computeItemLayout, defaultTransform, groupOriginShift, invert, multiply, type Mat2D } from '../core/geometry';
import { current } from 'immer';
import {
  Align,
  CHANNEL_BIO_MAX,
  CHANNEL_DISPLAY_NAME_MAX,
  GO_LIVE_NOTIFICATION_MAX,
  STREAM_CATEGORY_MAX,
  STREAM_TITLE_MAX,
  type FilterInstance,
  type GroupItem,
  type Id,
  type NestedSceneItem,
  type Project,
  type Scene,
  type SceneItem,
  type SettingsBag,
  type Source,
  type SourceItem,
  type Vec2,
} from '../core/model';
import {
  cleanAccentColor,
  cleanContentLabels,
  cleanFeatured,
  cleanClips,
  cleanGoal,
  cleanRetentionDays,
  cleanRaids,
  cleanFavourites,
  cleanLanguage,
  cleanLinks,
  cleanMultilineText,
  cleanPortalUrl,
  cleanSchedule,
  cleanTags,
  cleanText,
  httpsUrl,
} from '../core/channel';
import { cleanCaptions } from '../core/captions';
import type { Registry } from '../core/registry';
import { isSystemItem, isSystemSourceKind, pinSystemItems, SYSTEM_ITEM_REFUSAL } from '../core/system-items';
import type { AddItemOptions, Commands, FilterOwner, ItemLocation, ProjectStore } from './api';
import { createId, defaultAudioStrip } from './defaults';
import {
  boxCentre,
  chainMatrix,
  contentMatrixOf,
  makeMeasure,
  reexpress,
  restoreGroups,
  snapshotGroups,
  type ContentSizeOf,
} from './groupMath';
import { queries, uniqueAmong } from './queries';
import { clamp, isNum, sanitizeNonNegative, sanitizeTransformPatch, sanitizeVideoPatch } from './sanitize';

const { findItem, sourceRefCount, reachableScenes, uniqueName } = queries;

const measureFor = (p: Project, contentSizeOf: ContentSizeOf | undefined) =>
  makeMeasure(contentSizeOf, { x: p.video.width, y: p.video.height });

/** Groups from both chains, innermost first. */
function affectedGroups(a: readonly GroupItem[], b: readonly GroupItem[]): GroupItem[] {
  const byId = new Map<Id, { group: GroupItem; depth: number }>();
  for (const chain of [a, b]) chain.forEach((group, depth) => byId.set(group.id, { group, depth }));
  return [...byId.values()].sort((x, y) => y.depth - x.depth).map((e) => e.group);
}


function requireScene(p: Project, id: Id): Scene {
  const scene = p.scenes[id];
  if (!scene) throw new Error('That scene no longer exists.');
  return scene;
}

function requireSource(p: Project, id: Id): Source {
  const source = p.sources[id];
  if (!source) throw new Error('That source no longer exists.');
  return source;
}

function requireItem(p: Project, sceneId: Id, itemId: Id): ItemLocation {
  requireScene(p, sceneId);
  const loc = findItem(p, sceneId, itemId);
  if (!loc) throw new Error('That item no longer exists.');
  return loc;
}

/** Commands that hide, unlock, move, group, duplicate or remove a system layer refuse. */
function refuseSystem(p: Project, item: SceneItem): void {
  if (isSystemItem(p, item)) throw new Error(SYSTEM_ITEM_REFUSAL);
}

function cleanName(name: string, what: string): string {
  const trimmed = name.trim();
  if (!trimmed) throw new Error(`The ${what} name cannot be empty.`);
  return trimmed;
}

function containerOf(scene: Scene, loc: ItemLocation): SceneItem[] {
  return loc.parent ? loc.parent.children : scene.items;
}

function insertAt<T>(list: T[], value: T, index: number | undefined): void {
  const i = index === undefined || !isNum(index) ? list.length : clamp(Math.trunc(index), 0, list.length);
  list.splice(i, 0, value);
}

/** Removes matching items at any depth, in place, and returns them (with their subtrees). */
function prune(items: SceneItem[], drop: (item: SceneItem) => boolean): SceneItem[] {
  const removed: SceneItem[] = [];
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]!;
    if (drop(item)) removed.push(...items.splice(i, 1));
    else if (item.type === 'group') removed.push(...prune(item.children, drop));
  }
  return removed;
}

function collectSourceIds(items: readonly SceneItem[], into = new Set<Id>()): Set<Id> {
  for (const item of items) {
    if (item.type === 'source') into.add(item.sourceId);
    else if (item.type === 'group') collectSourceIds(item.children, into);
  }
  return into;
}

/** Only sources that just lost references are candidates; unplaced sources the user made stay. */
function collectGarbage(p: Project, candidates: Iterable<Id>): void {
  for (const id of candidates) if (p.sources[id] && sourceRefCount(p, id) === 0) delete p.sources[id];
}

function subtreeHas(item: SceneItem, id: Id): boolean {
  if (item.id === id) return true;
  return item.type === 'group' && item.children.some((c) => subtreeHas(c, id));
}

function itemBase(options: AddItemOptions = {}) {
  return {
    id: createId(),
    visible: options.visible ?? true,
    locked: false,
    transform: { ...defaultTransform(), ...sanitizeTransformPatch(options.transform ?? {}) },
    blendMode: 'normal' as const,
    scaleFilter: 'default' as const,
    opacity: 1,
  };
}

function cloneFilters(filters: readonly FilterInstance[]): FilterInstance[] {
  return filters.map((f) => ({ ...f, id: createId(), settings: structuredClone(f.settings) }));
}

/** The run state belongs to the original's running instance; a copy starts fresh. */
function cloneSource(source: Source, name: string): Source {
  const { runtimeState: _runState, ...rest } = source;
  return {
    ...rest,
    id: createId(),
    name,
    settings: structuredClone(source.settings),
    filters: cloneFilters(source.filters),
    audio: { ...source.audio },
  };
}

/** `item` must come from a frozen snapshot, never from a draft (structuredClone cannot clone proxies). */
function cloneItem(item: SceneItem, mapSource: (id: Id) => Id): SceneItem {
  const base = { ...item, id: createId(), transform: structuredClone(item.transform) };
  if (item.type === 'group') {
    return { ...base, type: 'group', name: item.name, children: item.children.map((c) => cloneItem(c, mapSource)) };
  }
  if (item.type === 'source') return { ...base, type: 'source', sourceId: mapSource(item.sourceId) } as SourceItem;
  return { ...base, type: 'scene', sceneId: item.sceneId } as NestedSceneItem;
}

function filtersOf(p: Project, owner: FilterOwner): FilterInstance[] {
  return owner.type === 'source' ? requireSource(p, owner.id).filters : requireScene(p, owner.id).filters;
}

function swapAlignment(alignment: number, low: number, high: number): number {
  const had = alignment & (low | high);
  const rest = alignment & ~(low | high);
  if (had === low) return rest | high;
  if (had === high) return rest | low;
  return alignment;
}

export function createCommands(store: ProjectStore, registry: Registry): Commands {
  const project = (): Project => store.getState().project;
  // Every mutation ends with system layers back on top, visible and locked,
  // whatever the command did to the items around them.
  const run: ProjectStore['mutate'] = (label, recipe, options) =>
    store.mutate(
      label,
      (d) => {
        recipe(d);
        pinSystemItems(d);
      },
      options,
    );

  const commands: Commands = {
    // Scenes
    addScene(name) {
      const id = createId();
      run('Add scene', (d) => {
        const base = name === undefined ? 'Scene' : cleanName(name, 'scene');
        d.scenes[id] = { id, name: uniqueName(d, base, 'scenes'), items: [], filters: [] };
        d.sceneOrder.push(id);
      });
      return id;
    },

    duplicateScene(sceneId) {
      const snapshot = requireScene(project(), sceneId);
      const id = createId();
      run('Duplicate scene', (d) => {
        d.scenes[id] = {
          id,
          name: uniqueName(d, snapshot.name, 'scenes'),
          items: snapshot.items.map((item) => cloneItem(item, (s) => s)),
          filters: cloneFilters(snapshot.filters),
        };
        d.sceneOrder.splice(d.sceneOrder.indexOf(sceneId) + 1, 0, id);
      });
      return id;
    },

    removeScene(sceneId) {
      run('Remove scene', (d) => {
        const scene = requireScene(d, sceneId);
        if (d.sceneOrder.length <= 1) throw new Error('A scene collection needs at least one scene.');
        const candidates = collectSourceIds(scene.items);
        const position = d.sceneOrder.indexOf(sceneId);
        delete d.scenes[sceneId];
        d.sceneOrder.splice(position, 1);
        for (const other of Object.values(d.scenes)) {
          prune(other.items, (item) => item.type === 'scene' && item.sceneId === sceneId);
        }
        if (d.currentSceneId === sceneId) {
          d.currentSceneId = d.sceneOrder[Math.min(position, d.sceneOrder.length - 1)]!;
        }
        collectGarbage(d, candidates);
      });
    },

    renameScene(sceneId, name) {
      run('Rename scene', (d) => {
        const scene = requireScene(d, sceneId);
        const next = cleanName(name, 'scene');
        if (Object.values(d.scenes).some((s) => s.id !== sceneId && s.name === next)) {
          throw new Error(`A scene named "${next}" already exists.`);
        }
        scene.name = next;
      });
    },

    moveScene(sceneId, toIndex) {
      run('Move scene', (d) => {
        requireScene(d, sceneId);
        d.sceneOrder.splice(d.sceneOrder.indexOf(sceneId), 1);
        insertAt(d.sceneOrder, sceneId, toIndex);
      });
    },

    setCurrentScene(sceneId) {
      run(
        'Switch scene',
        (d) => {
          requireScene(d, sceneId);
          d.currentSceneId = sceneId;
        },
        { undoable: false },
      );
    },

    // Sources
    createSource(kind, name, settings) {
      const id = createId();
      run('Add source', (d) => {
        d.sources[id] = newSource(d, id, kind, name, settings);
      });
      return id;
    },

    addSourceToScene(sceneId, kind, options = {}) {
      const sourceId = createId();
      const itemId = createId();
      run('Add source', (d) => {
        const scene = requireScene(d, sceneId);
        d.sources[sourceId] = newSource(d, sourceId, kind, options.name, options.settings);
        insertAt(scene.items, { ...itemBase(options), id: itemId, type: 'source', sourceId }, options.index);
      });
      return { sourceId, itemId };
    },

    addExistingSource(sceneId, sourceId, options) {
      const id = createId();
      run('Add source to scene', (d) => {
        const scene = requireScene(d, sceneId);
        requireSource(d, sourceId);
        insertAt(scene.items, { ...itemBase(options), id, type: 'source', sourceId }, options?.index);
      });
      return id;
    },

    addNestedScene(sceneId, nestedSceneId, options) {
      const id = createId();
      run('Nest scene', (d) => {
        const scene = requireScene(d, sceneId);
        requireScene(d, nestedSceneId);
        if (sceneId === nestedSceneId || reachableScenes(d, nestedSceneId).has(sceneId)) {
          throw new Error('A scene cannot contain itself, directly or through other nested scenes.');
        }
        insertAt(scene.items, { ...itemBase(options), id, type: 'scene', sceneId: nestedSceneId }, options?.index);
      });
      return id;
    },

    renameSource(sourceId, name) {
      run('Rename source', (d) => {
        const source = requireSource(d, sourceId);
        const next = cleanName(name, 'source');
        if (Object.values(d.sources).some((s) => s.id !== sourceId && s.name === next)) {
          throw new Error(`A source named "${next}" already exists.`);
        }
        source.name = next;
      });
    },

    updateSourceSettings(sourceId, patch, options) {
      run(
        'Change source settings',
        (d) => {
          const source = requireSource(d, sourceId);
          for (const [key, value] of Object.entries(patch)) {
            if (value === undefined) delete source.settings[key];
            else source.settings[key] = value;
          }
        },
        options,
      );
    },

    setSourceRuntimeState(sourceId, state) {
      run(
        'Save source state',
        (d) => {
          const source = requireSource(d, sourceId);
          if (state === null) delete source.runtimeState;
          else source.runtimeState = structuredClone(state);
        },
        { undoable: false },
      );
    },

    applyMigratedSettings(sourceId, settings, settingsVersion) {
      run(
        'Upgrade source settings',
        (d) => {
          const source = requireSource(d, sourceId);
          source.settings = structuredClone(settings);
          source.settingsVersion = settingsVersion;
        },
        { undoable: false },
      );
    },

    duplicateSource(sourceId) {
      const snapshot = requireSource(project(), sourceId);
      const id = createId();
      run('Duplicate source', (d) => {
        const copy = cloneSource(snapshot, uniqueName(d, snapshot.name, 'sources'));
        copy.id = id;
        d.sources[id] = copy;
      });
      return id;
    },

    removeSource(sourceId) {
      run('Remove source', (d) => {
        if (isSystemSourceKind(requireSource(d, sourceId).kind)) throw new Error(SYSTEM_ITEM_REFUSAL);
        for (const scene of Object.values(d.scenes)) {
          prune(scene.items, (item) => item.type === 'source' && item.sourceId === sourceId);
        }
        delete d.sources[sourceId];
      });
    },

    // Scene items
    updateItem(sceneId, itemId, patch) {
      run('Change item', (d) => {
        const { item } = requireItem(d, sceneId, itemId);
        if ((patch.visible === false || patch.locked === false) && isSystemItem(d, item)) throw new Error(SYSTEM_ITEM_REFUSAL);
        if (patch.visible !== undefined) item.visible = patch.visible;
        if (patch.locked !== undefined) item.locked = patch.locked;
        if (patch.blendMode !== undefined) item.blendMode = patch.blendMode;
        if (patch.scaleFilter !== undefined) item.scaleFilter = patch.scaleFilter;
        if (isNum(patch.opacity)) item.opacity = clamp(patch.opacity, 0, 1);
      });
    },

    setItemTransform(sceneId, itemId, patch, options) {
      run(
        'Transform item',
        (d) => {
          const { item } = requireItem(d, sceneId, itemId);
          Object.assign(item.transform, sanitizeTransformPatch(patch));
        },
        options,
      );
    },

    removeItems(sceneId, itemIds) {
      run('Remove items', (d) => {
        const scene = requireScene(d, sceneId);
        for (const id of itemIds) refuseSystem(d, requireItem(d, sceneId, id).item);
        const ids = new Set(itemIds);
        const removed = prune(scene.items, (item) => ids.has(item.id));
        collectGarbage(d, collectSourceIds(removed));
      });
    },

    duplicateItems(sceneId, itemIds, mode) {
      const snapshot = project();
      const ids = new Set(itemIds);
      const targets = itemIds
        .map((id) => requireItem(snapshot, sceneId, id))
        .map((loc) => (refuseSystem(snapshot, loc.item), loc))
        // A selected group already carries its selected descendants.
        .filter((loc, i, all) => all.findIndex((o) => o.item.id === loc.item.id) === i && !loc.ancestors.some((g) => ids.has(g.id)));
      const created: Id[] = [];
      run('Duplicate items', (d) => {
        const scene = requireScene(d, sceneId);
        const copies = new Map<Id, Id>();
        const mapSource = (sourceId: Id): Id => {
          const original = requireSource(snapshot, sourceId);
          if ((mode ?? registry.getSource(original.kind)?.duplicate ?? 'copy') === 'reference') return sourceId;
          let copyId = copies.get(sourceId);
          if (!copyId) {
            const copy = cloneSource(original, uniqueName(d, original.name, 'sources'));
            d.sources[copy.id] = copy;
            copyId = copy.id;
            copies.set(sourceId, copyId);
          }
          return copyId;
        };
        for (const loc of targets) {
          const clone = cloneItem(loc.item, mapSource);
          const live = findItem(d, sceneId, loc.item.id)!;
          containerOf(scene, live).splice(live.index + 1, 0, clone);
          created.push(clone.id);
        }
      });
      return created;
    },

    moveItem(sceneId, itemId, toIndex, groupId, contentSizeOf) {
      run('Move item', (d) => {
        const scene = requireScene(d, sceneId);
        const loc = requireItem(d, sceneId, itemId);
        refuseSystem(d, loc.item);
        let dest: ItemLocation | null = null;
        if (groupId) {
          dest = requireItem(d, sceneId, groupId);
          if (dest.item.type !== 'group') throw new Error('Items can only be moved into a group.');
          if (subtreeHas(loc.item, groupId)) throw new Error('A group cannot be moved into itself.');
        }
        const destParent = groupId === undefined ? loc.parent : ((dest?.item as GroupItem | undefined) ?? null);
        const crossing = (destParent?.id ?? null) !== (loc.parent?.id ?? null);
        const measure = measureFor(d, contentSizeOf);
        const oldChain = loc.ancestors;
        const newChain = dest ? [...dest.ancestors, dest.item as GroupItem] : [];
        const extent = measure(loc.item);
        const touched = crossing ? affectedGroups(oldChain, newChain) : [];
        const before = snapshotGroups(touched, measure);
        let change: Mat2D | null = null;
        if (crossing) {
          // The destination matrix is taken before the item joins, so the move is exact; the
          // groups are put back afterwards in case the new member changed their extent.
          const into = invert(chainMatrix(newChain, measure));
          if (!into) throw new Error('The target group has zero scale, so items cannot be moved into it.');
          change = multiply(into, chainMatrix(oldChain, measure));
        }
        containerOf(scene, loc).splice(loc.index, 1);
        if (change) reexpress(loc.item, extent, change);
        insertAt(destParent ? destParent.children : scene.items, loc.item, toIndex);
        restoreGroups(touched, before, measure);
      });
    },

    orderItem(sceneId, itemId, direction) {
      run('Reorder item', (d) => {
        const scene = requireScene(d, sceneId);
        const loc = requireItem(d, sceneId, itemId);
        refuseSystem(d, loc.item);
        const list = containerOf(scene, loc);
        const target =
          direction === 'top' ? list.length - 1 : direction === 'bottom' ? 0 : loc.index + (direction === 'up' ? 1 : -1);
        if (target < 0 || target >= list.length || target === loc.index) return;
        list.splice(loc.index, 1);
        list.splice(target, 0, loc.item);
      });
    },

    groupItems(sceneId, itemIds, name, contentSizeOf) {
      const groupId = createId();
      run('Group items', (d) => {
        const scene = requireScene(d, sceneId);
        const locs = [...new Set(itemIds)].map((id) => requireItem(d, sceneId, id));
        for (const loc of locs) refuseSystem(d, loc.item);
        const groupNames: string[] = [];
        for (const item of queries.flattenItems(scene)) if (item.type === 'group') groupNames.push(item.name);
        const groupName = name === undefined ? uniqueAmong(groupNames, 'Group') : cleanName(name, 'group');
        if (locs.length === 0) {
          // An empty group is a container to drag items into.
          scene.items.push({ ...itemBase(), id: groupId, type: 'group', name: groupName, children: [] });
          return;
        }
        const issue = queries.groupIssue(d, sceneId, itemIds);
        if (issue) throw new Error(issue);
        const list = containerOf(scene, locs[0]!);
        const indices = locs.map((l) => l.index).sort((a, b) => a - b);
        const children = indices.map((i) => list[i]!);
        const insertIndex = indices[indices.length - 1]! - (indices.length - 1);
        const measure = measureFor(d, contentSizeOf);
        for (let i = indices.length - 1; i >= 0; i--) list.splice(indices[i]!, 1);

        // The group origin is the top-left of the children's drawn boxes (their anchors when
        // sizes are unknown). The group is then a pure translation, so every child keeps its
        // exact on-screen place and has non-negative group-local coordinates.
        const points: Vec2[] = children.flatMap((c) => {
          const extent = measure(c);
          const shift = extent.min ? groupOriginShift(c.transform, extent.min) : { x: 0, y: 0 };
          return computeItemLayout(c.transform, extent.size).corners.map((p) => ({ x: p.x + shift.x, y: p.y + shift.y }));
        });
        const origin: Vec2 = {
          x: Math.min(...points.map((p) => p.x)),
          y: Math.min(...points.map((p) => p.y)),
        };
        for (const child of children) {
          child.transform.position = { x: child.transform.position.x - origin.x, y: child.transform.position.y - origin.y };
        }
        const group: GroupItem = {
          ...itemBase({ transform: { position: origin } }),
          id: groupId,
          type: 'group',
          name: groupName,
          children,
        };
        list.splice(insertIndex, 0, group);
      });
      return groupId;
    },

    ungroup(sceneId, groupId, contentSizeOf) {
      run('Ungroup', (d) => {
        const scene = requireScene(d, sceneId);
        const loc = requireItem(d, sceneId, groupId);
        const group = loc.item;
        if (group.type !== 'group') throw new Error('That item is not a group.');
        const measure = measureFor(d, contentSizeOf);
        const touched = [...loc.ancestors].reverse();
        const before = snapshotGroups(touched, measure);
        const into = contentMatrixOf(group, measure);
        const children = [...group.children];
        for (const child of children) {
          reexpress(child, measure(child), into);
          // The group's own state is carried down so nothing becomes editable or visible by ungrouping.
          child.visible = child.visible && group.visible;
          child.locked = child.locked || group.locked;
          child.opacity = child.opacity * group.opacity;
          if (child.blendMode === 'normal') child.blendMode = group.blendMode;
          if (child.scaleFilter === 'default') child.scaleFilter = group.scaleFilter;
        }
        containerOf(scene, loc).splice(loc.index, 1, ...children);
        restoreGroups(touched, before, measure);
      });
    },

    renameGroup(sceneId, groupId, name) {
      run('Rename group', (d) => {
        const { item } = requireItem(d, sceneId, groupId);
        if (item.type !== 'group') throw new Error('That item is not a group.');
        item.name = cleanName(name, 'group');
      });
    },

    fitItem(sceneId, itemId, mode, contentSize, contentSizeOf) {
      run('Fit item', (d) => {
        const loc = requireItem(d, sceneId, itemId);
        const { item } = loc;
        const canvas = { x: d.video.width, y: d.video.height };
        if (mode === 'reset') {
          item.transform = defaultTransform();
          return;
        }
        const measure = measureFor(d, contentSizeOf);
        const own = measure(item);
        const extent = { size: isNum(contentSize.x) && isNum(contentSize.y) ? contentSize : own.size, min: own.min };
        // Fit and centre are defined on the canvas: work in canvas space and express the
        // result in the parent group's space through that group's full matrix.
        const space = loc.parent ? chainMatrix(loc.ancestors, measure) : null;
        const inverse = space ? invert(space) : null;
        if (space && !inverse) throw new Error('The item sits in a group with zero scale.');
        const world = { transform: space ? current(item.transform) : item.transform };
        if (space) reexpress(world, extent, space);
        const t = world.transform;
        if (mode === 'fit' || mode === 'stretch') {
          t.position = { x: 0, y: 0 };
          t.alignment = Align.TOP | Align.LEFT;
          t.rotation = 0;
          t.boundsType = mode === 'fit' ? 'scale-inner' : 'stretch';
          t.boundsAlignment = Align.CENTER;
          t.bounds = canvas;
        } else {
          if (!isNum(extent.size.x) || !isNum(extent.size.y)) throw new Error('The item has no known size yet.');
          const centre = boxCentre(t, extent);
          t.position = {
            x: mode === 'center-vertical' ? t.position.x : t.position.x + canvas.x / 2 - centre.x,
            y: mode === 'center-horizontal' ? t.position.y : t.position.y + canvas.y / 2 - centre.y,
          };
        }
        if (inverse) {
          reexpress(world, extent, inverse);
          item.transform = world.transform;
        }
      });
    },

    flipItem(sceneId, itemId, axis) {
      run('Flip item', (d) => {
        const { item } = requireItem(d, sceneId, itemId);
        const t = item.transform;
        // A bounded item's box is the bounds whatever the scale's sign, and the
        // layout mirrors the fitted content inside its slot, so negating is enough.
        if (t.boundsType !== 'none') {
          t.scale = axis === 'horizontal' ? { x: -t.scale.x, y: t.scale.y } : { x: t.scale.x, y: -t.scale.y };
          return;
        }
        // Mirroring about the alignment anchor would move the box off its place, so the
        // anchor is moved to the opposite edge in the same step; the box stays put.
        if (axis === 'horizontal') {
          t.scale = { x: -t.scale.x, y: t.scale.y };
          t.alignment = swapAlignment(t.alignment, Align.LEFT, Align.RIGHT);
        } else {
          t.scale = { x: t.scale.x, y: -t.scale.y };
          t.alignment = swapAlignment(t.alignment, Align.TOP, Align.BOTTOM);
        }
      });
    },

    rotateItem(sceneId, itemId, deltaDegrees) {
      if (!isNum(deltaDegrees)) throw new Error('The rotation must be a number.');
      run('Rotate item', (d) => {
        const { item } = requireItem(d, sceneId, itemId);
        item.transform.rotation = (item.transform.rotation + deltaDegrees) % 360 || 0;
      });
    },

    // Filters
    addFilter(owner, kind, name) {
      const id = createId();
      run('Add filter', (d) => {
        const list = filtersOf(d, owner);
        const def = registry.getFilter(kind);
        const base = name === undefined ? (def?.displayName ?? kind) : cleanName(name, 'filter');
        list.push({
          id,
          kind,
          name: uniqueAmong(
            list.map((f) => f.name),
            base,
          ),
          enabled: true,
          settings: { ...def?.defaults() },
        });
      });
      return id;
    },

    removeFilter(owner, filterId) {
      run('Remove filter', (d) => {
        const list = filtersOf(d, owner);
        const index = list.findIndex((f) => f.id === filterId);
        if (index < 0) throw new Error('That filter no longer exists.');
        list.splice(index, 1);
      });
    },

    updateFilter(owner, filterId, patch, options) {
      run(
        'Change filter',
        (d) => {
          const filter = filtersOf(d, owner).find((f) => f.id === filterId);
          if (!filter) throw new Error('That filter no longer exists.');
          if (patch.name !== undefined) filter.name = cleanName(patch.name, 'filter');
          if (patch.enabled !== undefined) filter.enabled = patch.enabled;
          if (patch.settings) for (const [key, value] of Object.entries(patch.settings)) filter.settings[key] = value;
        },
        options,
      );
    },

    moveFilter(owner, filterId, toIndex) {
      run('Reorder filter', (d) => {
        const list = filtersOf(d, owner);
        const index = list.findIndex((f) => f.id === filterId);
        if (index < 0) throw new Error('That filter no longer exists.');
        const [filter] = list.splice(index, 1);
        insertAt(list, filter!, toIndex);
      });
    },

    // Audio
    setAudioStrip(sourceId, patch, options) {
      run(
        'Change audio',
        (d) => {
          const strip = requireSource(d, sourceId).audio;
          if (isNum(patch.volume)) strip.volume = Math.max(0, patch.volume);
          if (isNum(patch.balance)) strip.balance = clamp(patch.balance, -1, 1);
          if (isNum(patch.syncOffsetMs)) strip.syncOffsetMs = Math.max(0, patch.syncOffsetMs);
          if (patch.muted !== undefined) strip.muted = patch.muted;
          if (patch.forceMono !== undefined) strip.forceMono = patch.forceMono;
          if (patch.monitoring !== undefined) strip.monitoring = patch.monitoring;
        },
        options,
      );
    },

    // Transitions
    addTransition(kind, name) {
      const id = createId();
      run('Add transition', (d) => {
        const def = registry.getTransition(kind);
        const base = name === undefined ? (def?.displayName ?? kind) : cleanName(name, 'transition');
        d.transitions[id] = {
          id,
          kind,
          name: uniqueAmong(
            Object.values(d.transitions).map((t) => t.name),
            base,
          ),
          settings: { ...def?.defaults() },
        };
        d.transitionOrder.push(id);
      });
      return id;
    },

    updateTransition(id, patch) {
      run('Change transition', (d) => {
        const transition = d.transitions[id];
        if (!transition) throw new Error('That transition no longer exists.');
        if (patch.name !== undefined) transition.name = cleanName(patch.name, 'transition');
        if (patch.settings) for (const [key, value] of Object.entries(patch.settings)) transition.settings[key] = value;
      });
    },

    removeTransition(id) {
      run('Remove transition', (d) => {
        if (!d.transitions[id]) throw new Error('That transition no longer exists.');
        if (d.transitionOrder.length <= 1) throw new Error('At least one transition is required.');
        const position = d.transitionOrder.indexOf(id);
        delete d.transitions[id];
        d.transitionOrder.splice(position, 1);
        if (d.currentTransitionId === id) {
          d.currentTransitionId = d.transitionOrder[Math.min(position, d.transitionOrder.length - 1)]!;
        }
      });
    },

    setCurrentTransition(id) {
      run('Select transition', (d) => {
        if (!d.transitions[id]) throw new Error('That transition no longer exists.');
        d.currentTransitionId = id;
      });
    },

    setTransitionDuration(durationMs) {
      if (!isNum(durationMs)) return;
      run('Set transition duration', (d) => {
        d.transitionDurationMs = Math.max(0, durationMs);
      });
    },

    // Settings
    renameProject(name) {
      run('Rename project', (d) => {
        d.name = cleanName(name, 'project');
      });
    },

    setVideoSettings(patch) {
      run('Change video settings', (d) => {
        const previousHeight = d.video.height;
        Object.assign(d.video, sanitizeVideoPatch(patch));
        // Layout is stored in canvas pixels, so a new base resolution rescales it.
        // Like OBS, everything follows the canvas height; a changed aspect ratio
        // leaves extra or missing width at the right edge.
        const factor = d.video.height / previousHeight;
        if (factor === 1 || !Number.isFinite(factor)) return;
        for (const scene of Object.values(d.scenes)) {
          // Group children live in the group's local space and follow its scale.
          for (const item of scene.items) {
            const t = item.transform;
            t.position.x *= factor;
            t.position.y *= factor;
            t.bounds.x *= factor;
            t.bounds.y *= factor;
            if (t.boundsType === 'none') {
              t.scale.x *= factor;
              t.scale.y *= factor;
            }
          }
        }
      });
    },

    setAudioSettings(patch, options) {
      run(
        'Change audio settings',
        (d) => {
          if (patch.sampleRate === 44100 || patch.sampleRate === 48000) d.audio.sampleRate = patch.sampleRate;
          if (isNum(patch.masterVolume)) d.audio.masterVolume = Math.max(0, patch.masterVolume);
          if (patch.masterMuted !== undefined) d.audio.masterMuted = patch.masterMuted;
          if (patch.monitorDeviceId !== undefined) d.audio.monitorDeviceId = patch.monitorDeviceId;
        },
        options,
      );
    },

    setStreamingSettings(patch) {
      // Output settings (endpoints, tokens) stay out of the undo stack so Ctrl+Z
      // while editing a scene can never silently revert them.
      run(
        'Change streaming settings',
        (d) => {
          const clean = sanitizeNonNegative(patch, ['maxViewers', 'videoKbps', 'audioKbps']);
          if (clean.maxViewers !== undefined) clean.maxViewers = Math.trunc(clean.maxViewers);
          if (clean.guestApproval !== undefined && clean.guestApproval !== 'lobby' && clean.guestApproval !== 'auto') delete clean.guestApproval;
          if (clean.guestLink !== undefined && typeof clean.guestLink !== 'boolean') delete clean.guestLink;
          if (clean.captions !== undefined) clean.captions = cleanCaptions({ ...d.output.streaming.captions, ...clean.captions });
          for (const [key, value] of Object.entries(clean)) {
            if (value !== undefined) (d.output.streaming as unknown as SettingsBag)[key] = value;
          }
        },
        { undoable: false },
      );
    },

    setRecordingSettings(patch) {
      run(
        'Change recording settings',
        (d) => {
          const clean = sanitizeNonNegative(patch, ['videoKbps', 'audioKbps']);
          for (const [key, value] of Object.entries(clean)) {
            if (value !== undefined) (d.output.recording as unknown as SettingsBag)[key] = value;
          }
        },
        { undoable: false },
      );
    },

    setStreamInfo(patch) {
      run(
        'Change stream info',
        (d) => {
          const info = d.channel.info;
          if (patch.title !== undefined) info.title = cleanText(patch.title, STREAM_TITLE_MAX);
          if (patch.goLiveNotification !== undefined) info.goLiveNotification = cleanText(patch.goLiveNotification, GO_LIVE_NOTIFICATION_MAX);
          if (patch.category !== undefined) info.category = cleanText(patch.category, STREAM_CATEGORY_MAX);
          if (patch.tags !== undefined) info.tags = cleanTags(patch.tags);
          if (patch.language !== undefined) info.language = cleanLanguage(patch.language);
          if (patch.contentLabels !== undefined) info.contentLabels = cleanContentLabels(patch.contentLabels);
          if (typeof patch.rerun === 'boolean') info.rerun = patch.rerun;
        },
        { undoable: false },
      );
    },

    setChannelProfile(patch) {
      run(
        'Change channel profile',
        (d) => {
          const profile = d.channel.profile;
          if (patch.displayName !== undefined) profile.displayName = cleanText(patch.displayName, CHANNEL_DISPLAY_NAME_MAX);
          if (patch.bio !== undefined) profile.bio = cleanMultilineText(patch.bio, CHANNEL_BIO_MAX);
          if (patch.links !== undefined) profile.links = cleanLinks(patch.links);
          if (patch.accentColor !== undefined) profile.accentColor = cleanAccentColor(patch.accentColor);
          if (patch.avatarUrl !== undefined) profile.avatarUrl = httpsUrl(patch.avatarUrl);
          if (patch.bannerUrl !== undefined) profile.bannerUrl = httpsUrl(patch.bannerUrl);
          if (patch.offlineImageUrl !== undefined) profile.offlineImageUrl = httpsUrl(patch.offlineImageUrl);
        },
        { undoable: false },
      );
    },

    setChannelSettings(patch) {
      run(
        'Change channel settings',
        (d) => {
          if (typeof patch.saveBroadcasts === 'boolean') d.channel.saveBroadcasts = patch.saveBroadcasts;
          if (typeof patch.disconnectProtection === 'boolean') d.channel.disconnectProtection = patch.disconnectProtection;
          if (patch.portalUrl !== undefined) d.channel.portalUrl = cleanPortalUrl(patch.portalUrl);
          if (patch.vodRetentionDays !== undefined) d.channel.vodRetentionDays = cleanRetentionDays(patch.vodRetentionDays);
        },
        { undoable: false },
      );
    },

    setChannelSchedule(entries) {
      run(
        'Change stream schedule',
        (d) => {
          d.channel.schedule = cleanSchedule(entries);
        },
        { undoable: false },
      );
    },

    setChannelFeatured(patch) {
      run(
        'Change featured content',
        (d) => {
          d.channel.featured = cleanFeatured({ ...d.channel.featured, ...patch });
        },
        { undoable: false },
      );
    },

    setClipSettings(patch) {
      run(
        'Change clip settings',
        (d) => {
          d.channel.clips = cleanClips({ ...d.channel.clips, ...patch });
        },
        { undoable: false },
      );
    },

    setChannelGoal(goal) {
      run(
        'Change channel goal',
        (d) => {
          d.channel.goal = goal === null ? null : cleanGoal(goal);
        },
        { undoable: false },
      );
    },

    setRaidSettings(patch) {
      run(
        'Change raid settings',
        (d) => {
          d.channel.raids = cleanRaids({ ...d.channel.raids, ...patch });
        },
        { undoable: false },
      );
    },

    setChannelFavourites(favourites) {
      run(
        'Change favourites',
        (d) => {
          d.channel.favourites = cleanFavourites(favourites);
        },
        { undoable: false },
      );
    },

    setHotkey(action, chords) {
      run(
        'Change hotkey',
        (d) => {
          if (chords.length === 0) delete d.hotkeys[action];
          else d.hotkeys[action] = [...new Set(chords)];
        },
        { undoable: false },
      );
    },

    setPlugins(plugins) {
      // Not undoable: undo cannot unload running plugin code, so the document would lie.
      run(
        'Change plugins',
        (d) => {
          d.plugins = plugins.map((p) => ({ ...p }));
        },
        { undoable: false },
      );
    },

    setPluginData(pluginId, data) {
      run(
        'Change plugin data',
        (d) => {
          d.pluginData[pluginId] = structuredClone(data);
        },
        { undoable: false },
      );
    },
  };

  function newSource(d: Project, id: Id, kind: string, name: string | undefined, settings: SettingsBag | undefined): Source {
    const def = registry.getSource(kind);
    const base = name === undefined ? (def?.displayName ?? kind) : cleanName(name, 'source');
    return {
      id,
      name: uniqueName(d, base, 'sources'),
      kind,
      settings: { ...def?.defaults(), ...settings },
      filters: [],
      audio: defaultAudioStrip(),
      // Stamped so a new source is not run through migrate() from version 0.
      ...(def?.settingsVersion !== undefined ? { settingsVersion: def.settingsVersion } : {}),
    };
  }

  return commands;
}
